<template>
  <div class="member-history">
    <div v-if="pm.lastSharingInvite?.isPending" class="member-task share-invite done">
      <div class="task-icon">
        <img src="@/assets/icons/mail_gray_60.png" />
      </div>
      <div>
        <div class="task-done">
          Share profile requested
          <span v-if="pm.lastSharingInvite.result == InvitationResult.Cancelled" class="cancelled">(cancelled)</span>
          <button type="button" class="btn btn-inline btn-link" @click="emit('viewSharingInvite', pm.lastSharingInvite!.id!)">
            View
          </button>
        </div>
        <div>
          <span class="task-date">
            {{ DateTimeUtil.toRelativePastDate(pm.lastSharingInvite?.lastSentDate) }}
          </span>
          <span v-if="sharingInviteByRef" class="task-actor">
            by <router-link :to="sharingInviteByRef.pageUrl">
              {{ sharingInviteByRef.displayName }}
            </router-link>
          </span>
        </div>
      </div>
    </div>
    <div v-if="props.pm.isProfileShared" class="member-task share done">
      <div class="task-icon">
        <img src="@/assets/icons/person_dotted_80.png"/>
        <img v-if="pm.isProfileShared" src="@/assets/icons/person_gray_150.png"/>
      </div>
      <div>
        <div class="task-done">
          Profile shared
        </div>
        <div>
          <span class="task-date">
            {{ DateTimeUtil.toRelativePastDate(pm.sharedProfileMember?.modifiedDate) }}
          </span>
          <span v-if="sharedByRef" class="task-actor">
            by <router-link :to="sharedByRef.pageUrl">
              {{ sharedByRef.displayName }}
            </router-link>
          </span>
        </div>
      </div>
    </div>
    <div v-if="pm.lastGroupInvite" class="member-task invite done">
      <div class="task-icon">
        <img src="@/assets/icons/mail_gray_60.png" />
      </div>
      <div>
        <div class="task-done">
          Invited to join
          <span v-if="pm.lastGroupInvite?.result == InvitationResult.Cancelled" class="cancelled">(cancelled)</span>
          <button type="button" v-if="pm.lastGroupInvite" class="btn btn-inline btn-link" @click="emit('viewGroupInvite', pm.lastGroupInvite.id!)">
            View
          </button>
        </div>
        <div>
          <span class="task-date">
            {{ DateTimeUtil.toRelativePastDate(pm.lastGroupInvite?.lastSentDate) }}
          </span>
          <span v-if="groupInviteByRef" class="task-actor">
            by <router-link :to="groupInviteByRef.pageUrl">
              {{ groupInviteByRef.displayName }}
            </router-link>
          </span>
        </div>
      </div>
    </div>
    <div v-if="pm.member" class="member-task joined done">
      <div class="task-icon">
        <img src="@/assets/icons/person_gray_150.png"/>
      </div>
      <div>
        <div class="task-done">
          Joined group
        </div>
        <div>
          <span class="task-date">
            {{ DateTimeUtil.toRelativePastDate(pm.member?.createdDate) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.member-history {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.member-task {
  display: grid;
  grid-template-columns: 28px 1fr;
  column-gap: 0.5rem;
  line-height: 1;

  .task-icon {
    width: max-content;
    height: 20px;
    justify-self: center;
  }

  .btn-link {
    margin-left: 0.5rem;
    font-size: 0.875rem;
  }

  &:not(.done) {
    .task-done, .task-date, .task-actor {
      display: none;
    }
  }

  &.done .task-not-done {
    display: none;
  }

  .task-date, .task-actor {
    font-size: 0.75rem;
    color: #aaa;

    a {
      color: #aaa;

      &:hover {
        color: #333;
      }
    }
  }

  .cancelled {
    font-size: 0.875rem;
    color: #888;
  }

  &.share {

    .task-icon img {
      width: 20px;
      height: 20px;
    }

    &.done .task-icon {
      :nth-child(2) {
        margin-left: -12px;
        filter: brightness(3.8);
      }
    }
  }

  &.share-invite, &.invite {
    img {
      width: 1.2rem;
      opacity: 30%;
    }

    &.done img {
      opacity: 1;
      filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
    }
  }

  &.add-user .task-icon {
    img {
      width: 20px;
      height: 20px;
      filter: brightness(3.8);
    }

    :nth-child(2) {
        margin-left: -12px;
        filter: brightness(2.5);
    }
  }

  &.joined .task-icon {
    img {
      width: 20px;
      height: 20px;
      filter: brightness(2.5);
    }
  }
}
</style>

<script lang="ts" setup>
import { computed } from 'vue'
import { PotentialMember } from './FamilyGroupMemberManager'
import { DateTimeUtil } from '@/util/LuxonUtil'
import { useUserStore } from '@/gp/UserStore'
import { LoadMode } from '@/util/AsyncData'
import { InvitationResult } from '@/gp/GroupAdminModel'

const props = defineProps<{
  familyGroupId: string
  pm: PotentialMember
}>()

const emit = defineEmits<{
  (e: 'removeProfile'): void
  (e: 'viewGroupInvite', inviteId: string): void
  (e: 'viewSharingInvite', inviteId: string): void
}>()

const userStore = useUserStore()

const sharingInviteByRef = computed(() => userStore.getAsyncUser(props.pm.lastSharingInvite?.senderUserId, LoadMode.EnsureLoaded)?.data)
const sharedByRef = computed(() => userStore.getAsyncUser(props.pm.sharedProfileMember?.modifiedUserId, LoadMode.EnsureLoaded)?.data)
const groupInviteByRef = computed(() => userStore.getAsyncUser(props.pm.lastGroupInvite?.senderUserId, LoadMode.EnsureLoaded)?.data)

</script>
